import React, { useContext, useState, useCallback, useEffect } from 'react';
import { IAppContext, AppContext } from 'appState/appContext';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { Form } from 'antd';
import { NewButton } from 'components/Button/NewButton/NewButton';
import { FormInputNew } from 'components/Input/FormInputNew';
import Text from 'components/Text/Text';
import { AuthLayout } from '../components/AuthLayout';
import style from '../components/AuthFormCard.module.scss';

// open id or oauth 2.0

type ComponentState = 'waitingDataEntry' | 'waitingServerResponse' | 'errorServerResponse';
type ValidateStatus = Parameters<typeof Form.Item>[0]['validateStatus'];
// type ValidateStatus = '' | 'success' | 'warning' | 'error' | 'validating' | undefined;

interface IFormStatus {
	componentState: ComponentState;
	validateStatus?: ValidateStatus;
	errorMessage?: string | null;
}

export const LoginPage: React.FC = () => {
	const { logIn } = useContext<IAppContext>(AppContext);
	const [userAuthStatus, setUserAuthStatus] = useState<IFormStatus>({ componentState: 'waitingDataEntry' });
	const [emailWithoutSpaces, setEmailWithoutSpaces] = useState<string>('');
	const { t: tLogin } = useTranslation('login');
	const history = useHistory();

	const goToHomePage = useCallback(() => {
		history.push('/home');
	}, []);

	const handleForgotPassword = useCallback(() => {
		history.push('/password-reset');
	}, []);

	const handleSubmitAuthData = async (loginDetails: { email: string; password: string }) => {
		setUserAuthStatus({ componentState: 'waitingServerResponse' });
		const statusAuthorization = await logIn(loginDetails.email, loginDetails.password);
		if (statusAuthorization === 'success') {
			goToHomePage();
		} else {
			setUserAuthStatus({
				componentState: 'errorServerResponse',
				validateStatus: 'error',
				errorMessage: tLogin('validation.credentialInvalid'),
			});
		}
	};

	const handleFieldsErrorReset = () => {
		const shouldCleanError = Object.keys(userAuthStatus).includes('validateStatus');
		if (shouldCleanError) {
			setUserAuthStatus({ componentState: 'waitingDataEntry' });
		}
	};

	useEffect(() => {
		const crutch = emailWithoutSpaces.replace(/\s/g, '');
		setEmailWithoutSpaces(crutch);
	}, [emailWithoutSpaces]);

	const isSignInDisabled =
		userAuthStatus.componentState === 'waitingServerResponse' ||
		userAuthStatus.componentState === 'errorServerResponse';

	return (
		<AuthLayout dataTest="LoginPage">
			<div className={style.formCard}>
				<Text translationKey={tLogin('signIn')} size="xl" weight="bold" />
				<Form
					name="basic"
					colon={false}
					className={style.formBody}
					onFinish={handleSubmitAuthData}
					onChange={handleFieldsErrorReset}
					fields={[
						{
							name: ['email'],
							value: emailWithoutSpaces,
						},
					]}
					layout="vertical"
					autoComplete="off"
					data-test="form">
					<div className={style.fieldsGroup}>
						<FormInputNew
							name="email"
							label="email"
							translationNameSpace="login"
							placeholder="email"
							validateStatus={userAuthStatus.validateStatus}
							help={userAuthStatus.errorMessage}
							validationRules={[
								{ required: true, message: tLogin('validation.enterCorrectValue.email') },
							]}
							onChange={(value) => setEmailWithoutSpaces(value.replace(/\s/g, ' '))}
						/>

						<FormInputNew
							name="password"
							label="password"
							type="password"
							translationNameSpace="login"
							placeholder="password"
							validateStatus={userAuthStatus.validateStatus}
							help={userAuthStatus.errorMessage}
							validationRules={[
								{ required: true, message: tLogin('validation.enterCorrectValue.password') },
							]}
							autoComplete="true" // May have to be removed later.
						/>
					</div>

					<div className={style.actionsRow} data-test="btns">
						<NewButton variant="ghost" color="purple" size="small" onClick={handleForgotPassword}>
							{tLogin('forgotPassword')}
						</NewButton>
						<NewButton
							type="submit"
							variant="primary"
							color="purple"
							size="small"
							disabled={isSignInDisabled}
							loading={userAuthStatus.componentState === 'waitingServerResponse'}>
							{tLogin('signIn')}
						</NewButton>
					</div>
				</Form>
			</div>
		</AuthLayout>
	);
};
